// src/components/Leaderboard.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient'; // Adjust path if needed
// import { statusColors } from '../colleges'; // Remove this if college_applications isn't in 'profiles'
// Import the new utility functions
import { getLogoPath, getCollegeInitials } from '../utils/collegeUtils';
// Import topColleges for the filter dropdown
import { topColleges } from '../colleges'; // Adjust path if needed
import { Link, useNavigate } from 'react-router-dom'; // Import Link
import { getPricingDisplayText, getDetailedPricingBreakdown, canAffordEssays } from '../utils/essayPricing';

import './Leaderboard.css';

const DEFAULT_PROFILE_LIMIT = 25;
const PROFILE_PAGE_SIZE = 1000;

// First, let's create a function to get theme-aware styles
const getThemeAwareStyles = () => {
  // Base styles object
  return {
    leaderboard: {
      position: 'relative',
      maxWidth: '800px',
      margin: '2.5rem auto',
      padding: '1.5rem',
      paddingTop: '1.8rem',
      backgroundColor: 'var(--card-bg, #fff)',
      borderRadius: '12px',
      boxShadow: '0 8px 30px rgba(0,0,0,0.07), 0 2px 8px rgba(0,0,0,0.03)',
      fontFamily: 'var(--font-sans, sans-serif)',
      overflow: 'visible',
    },
    title: {
      textAlign: 'center',
      marginBottom: '1.5rem',
      color: 'var(--text-color-primary, #333)',
    },
    loadingError: {
      textAlign: 'center',
      padding: '2rem',
      color: 'var(--text-color-secondary, #666)',
    },
    errorText: {
      color: 'var(--error-color, #d9534f)', // Red color for errors
    },
    leaderboardList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    leaderboardItem: {
      display: 'flex',
      alignItems: 'flex-start', // Align items to the top
      padding: '1rem 0.5rem',
      borderBottom: '1px solid var(--border-color, #eee)',
      gap: '1rem', // Space between rank and info
    },
    rank: {
      fontWeight: 'bold',
      fontSize: '1.2em',
      color: 'var(--accent-color, #007bff)',
      minWidth: '30px', // Ensure rank alignment
      textAlign: 'center',
      paddingTop: '0.2rem', // Adjust vertical alignment slightly
    },
    profileInfo: {
      flexGrow: 1, // Take remaining space
    },
    profileHeader: {
      margin: '0 0 0.5rem 0',
      fontSize: '1.1em',
      color: 'var(--text-color-primary, #333)',
    },
    profileDetail: {
      margin: '0.3rem 0',
      fontSize: '0.9em',
      color: 'var(--text-color-secondary, #555)',
      lineHeight: 1.4,
    },
    profileLabel: {
      fontWeight: '600', // Slightly bolder labels
      color: 'var(--text-color-primary, #333)',
      marginRight: '0.5em', // Add space after label
    },
    ecList: {
      listStyle: 'none', // Remove default bullets to avoid double bullets
      paddingLeft: '1.5em', // Indent the list
      margin: '0.3rem 0', // Match vertical margin of profileDetail <p>
      fontSize: '0.9em', // Match profileDetail font size
      color: 'var(--text-color-secondary, #555)', // Match profileDetail color
      lineHeight: 1.4, // Match profileDetail line height
    },
    ecListItem: {
      marginBottom: '0.25em', // Small space between ECs
    },
    rating: {
      display: 'inline-block', // Allows margin/padding
      marginTop: '0.5rem',
      fontWeight: 'bold',
      fontSize: '1em',
      color: 'var(--accent-color-dark, #0056b3)',
    },
    essayPricing: {
      marginTop: '0.75rem',
      padding: '0.5rem',
      backgroundColor: 'var(--bg-neutral-soft, #f9fafb)',
      borderRadius: '6px',
      border: '1px solid var(--border-color-soft, #e5e7eb)',
      fontSize: '0.9em',
    },
    essayPricingLabel: {
      fontWeight: '600',
      color: 'var(--text-color-primary, #333)',
      marginRight: '0.5rem',
    },
    essayPricingValue: {
      color: 'var(--accent-color, #007bff)',
      fontWeight: '500',
    },
    creditInfo: {
      marginTop: '0.5rem',
      textAlign: 'center',
      fontSize: '0.85em',
    },
    profileActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    unlockEssaysButton: {
      backgroundColor: 'var(--accent-tertiary, #ffc107)',
      color: 'var(--text-dark, #333)',
      border: 'none',
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: '500',
      transition: 'all 0.2s ease',
    },
    refreshButton: {
      display: 'block',
      margin: '1.5rem auto 0',
      padding: '0.6rem 1.2rem',
      cursor: 'pointer',
      backgroundColor: 'var(--button-secondary-bg, #6c757d)',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      fontSize: '0.9em',
      transition: 'background-color 0.2s ease',
    },
    // Add hover effect for button
    refreshButtonHover: {
       backgroundColor: 'var(--button-secondary-hover-bg, #5a6268)',
    },
    // Placeholder for when list is empty
    emptyList: {
      textAlign: 'center',
      padding: '2rem',
      color: 'var(--text-color-secondary, #666)',
    },
    // --- UPDATED STYLES for College Results ---
    collegeResultsSection: {
      marginTop: '1rem',
      paddingTop: '0.75rem',
      borderTop: '1px dashed var(--border-color-soft, #f3f4f6)',
    },
    collegeResultsTitle: {
      fontSize: '0.95em',
      fontWeight: '600',
      color: 'var(--text-color-primary, #333)',
      marginBottom: '0.8rem',
    },
    collegeResultsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '0.8rem',
    },
    acceptedSchoolWrapper: {
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      marginRight: '0.7rem',
      marginBottom: '0.7rem',
    },
    collegeLogoContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '52px',
      height: '52px',
      borderRadius: '8px',
      overflow: 'hidden',
      verticalAlign: 'middle',
      flexShrink: 0,
      backgroundColor: 'var(--bg-neutral-soft, #f9fafb)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    },
    collegeLogo: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      transition: 'transform 0.2s ease-out',
    },
    collegeLogoFallback: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      fontSize: '1.2em',
      fontWeight: 'bold',
      backgroundColor: 'var(--bg-neutral-alt, #f3f4f6)',
      color: 'var(--text-medium, #6b7280)',
      borderRadius: '8px',
      textAlign: 'center',
      lineHeight: '52px',
      textTransform: 'uppercase',
      flexShrink: 0,
    },
    committedCheckmark: {
      color: 'var(--accent-success, #16a34a)',
      fontSize: '1.6em',
      fontWeight: 'bold',
      marginLeft: '6px',
      verticalAlign: 'middle',
    },
    collegeResultDetail: {
      fontSize: '0.85em',
      fontStyle: 'italic',
      color: 'var(--text-color-secondary, #666)',
      margin: '0.2rem 0',
    },
    collegeResultsPlaceholder: {
      marginTop: '1rem',
      padding: '0.75rem',
      borderTop: '1px dashed var(--border-color-soft, #f3f4f6)',
      textAlign: 'center',
      backgroundColor: 'var(--bg-neutral-alt, #f9fafb)',
      borderRadius: 'var(--radius, 6px)',
      fontSize: '0.85em',
      color: 'var(--text-medium, #666)',
    },
    // --- Updated Filter Styles with New Positioning ---
    filterSection: {
      marginBottom: '1.5rem',
      textAlign: 'center', // Center everything
      position: 'relative',
    },
    filterContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      background: 'var(--bg-neutral-soft, linear-gradient(to right, rgba(249,250,251,0.8), rgba(255,255,255,0.9)))',
      borderRadius: '30px',
      padding: '0.5rem 1.2rem',
      boxShadow: '0 2px 10px rgba(0,0,0,0.03), 0 0 1px rgba(0,0,0,0.1)',
      border: '1px solid var(--border-color, rgba(229,231,235,0.7))',
      transition: 'all 0.2s ease',
    },
    filterLabel: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: 'var(--text-medium, #4b5563)',
      marginRight: '0.7rem',
    },
    filterSelect: {
      appearance: 'none',
      background: 'transparent',
      border: 'none',
      borderRadius: '20px',
      padding: '0.35rem 0.7rem',
      paddingRight: '2rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: 'var(--accent-color, #007bff)',
      cursor: 'pointer',
      outline: 'none',
      minWidth: '140px',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23007bff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 8px center',
      backgroundSize: '14px',
    },
    // Message for users who haven't submitted profiles
    filterMessage: {
      fontSize: '0.85rem',
      color: 'var(--text-light, #6b7280)',
      marginTop: '0.7rem',
      fontStyle: 'italic',
      textAlign: 'center',
      padding: '0.4rem 1rem',
      maxWidth: '400px',
      margin: '0.7rem auto 0',
      borderRadius: '8px',
      background: 'var(--bg-neutral-alt, #f3f4f6)',
    },
    unlockButton: {
      marginLeft: '1rem',
      padding: '0.3rem 0.8rem',
      fontSize: '0.8em',
      cursor: 'pointer',
      backgroundColor: 'var(--accent-secondary, #3b82f6)',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      transition: 'background-color 0.2s',
    },
    unlockButtonDisabled: {
      backgroundColor: 'var(--button-disabled, #ccc)',
      cursor: 'not-allowed',
    },
    viewProfileButton: {
      display: 'inline-block',
      marginTop: '0.75rem',
      padding: '0.5rem 1.2rem',
      fontSize: '0.9em',
      fontWeight: '500',
      backgroundColor: 'var(--accent-success, #28a745)', // Green for unlocked
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      textDecoration: 'none', // For Link
      textAlign: 'center',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
    },
    unlockInfo: {
      fontSize: '0.75em',
      color: 'var(--text-medium, #666)',
      marginLeft: '0.5em',
    }
  };
};

// Use the function to get styles, use this variable instead of "styles"
const styles = getThemeAwareStyles();

export default function Leaderboard() {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(DEFAULT_PROFILE_LIMIT); // Number of top profiles to show, or 'all'
  // State to track current user's submission status
  const [userHasSubmittedProfile, setUserHasSubmittedProfile] = useState(false);
  const [checkingUserStatus, setCheckingUserStatus] = useState(true);

  // --- State for Filtering ---
  const [filterCollege, setFilterCollege] = useState(''); // '' means 'All Schools'
  const [availableColleges, setAvailableColleges] = useState([]);

  const [unlockCount, setUnlockCount] = useState(0);
  const [unlockedProfileIds, setUnlockedProfileIds] = useState(new Set());
  const [currentUserId, setCurrentUserId] = useState(null); // Store current user's ID
  const [userCredits, setUserCredits] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);

  // Helper function to show success messages
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 4000); // Hide after 4 seconds
  };

  // Add CSS animation for success message
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromRight {
        0% {
          transform: translateX(100%);
          opacity: 0;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Match the app's explicit light/dark theme instead of the device preference.
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('dark'));

  useEffect(() => {
    const syncTheme = () => setIsDarkMode(document.body.classList.contains('dark'));
    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Fetch current user's status
  const fetchUserStatus = useCallback(async () => {
    setCheckingUserStatus(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userData, error } = await supabase
          .from('users')
          .select('submitted_profile')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        console.log("User submission status:", userData?.submitted_profile);
        
        // Force a refresh of status if there's a profile but status doesn't match
        if (!userData?.submitted_profile) {
          // Check if the user actually has a profile
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id')
            .eq('is_archived', false)
            .eq('user_id', user.id)
            .limit(1);
            
          if (!profileError && profileData && profileData.length > 0) {
            // User has a profile but submitted_profile is false - fix this
            console.log("Found profile but status is not submitted, repairing...");
            const { error: updateError } = await supabase
              .from('users')
              .update({ submitted_profile: true })
              .eq('id', user.id);
              
            if (!updateError) {
              // Status fixed, set to true locally
              setUserHasSubmittedProfile(true);
            } else {
              console.error("Failed to repair profile status:", updateError);
              setUserHasSubmittedProfile(userData?.submitted_profile || false);
            }
          } else {
            setUserHasSubmittedProfile(userData?.submitted_profile || false);
          }
        } else {
          setUserHasSubmittedProfile(userData?.submitted_profile || false);
        }
      } else {
        setUserHasSubmittedProfile(false); // Not logged in
      }
    } catch (error) {
      console.error("Error fetching user status:", error);
      setUserHasSubmittedProfile(false); // Default to false on error
    } finally {
      setCheckingUserStatus(false);
    }
  }, []);

  // Fetch leaderboard data (including college_results)
  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    console.log(limit === 'all' ? 'Fetching all profiles...' : `Fetching top ${limit} profiles...`);
    try {
      const selectFields = `
        id, rating, class_year, essay, gpa, sat_score, act_score,
        ap_num_taken, ap_num_fives, extracurriculars, awards,
        college_results
      `;
      let data = [];

      if (limit === 'all') {
        // Supabase caps a single response, so page until every profile is loaded.
        let from = 0;
        while (true) {
          const { data: page, error: queryError } = await supabase
            .from('profiles')
            .select(selectFields)
            .eq('is_archived', false)
            .order('rating', { ascending: false })
            .range(from, from + PROFILE_PAGE_SIZE - 1);

          if (queryError) {
            throw new Error(`Database error fetching leaderboard: ${queryError.message}. Check RLS.`);
          }

          data = data.concat(page || []);
          if (!page || page.length < PROFILE_PAGE_SIZE) break;
          from += PROFILE_PAGE_SIZE;
        }
      } else {
        const { data: topProfiles, error: queryError } = await supabase
          .from('profiles')
          .select(selectFields)
            .eq('is_archived', false)
          .order('rating', { ascending: false })
          .limit(limit);

        if (queryError) {
          throw new Error(`Database error fetching leaderboard: ${queryError.message}. Check RLS.`);
        }
        data = topProfiles || [];
      }

      console.log("Fetched profiles:", data);
      setLeaders(data);

    } catch (e) {
      console.error("Failed to load leaderboard:", e);
      setError(e.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // Fetch user ID and unlock info
  const fetchUserUnlockInfo = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return; // Not logged in
      setCurrentUserId(user.id);

      console.log('DEBUG: Fetching unlock info for user ID:', user.id);

      // Try API endpoint first
      try {
        const response = await fetch(`/api/user-unlock-info?user_id=${user.id}`, { 
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('DEBUG: API response for unlock info:', data);
          setUnlockCount(data.unlockCount || 0);
          setUnlockedProfileIds(new Set(data.unlockedProfileIds || []));
          return; // Success, exit early
        } else {
          console.warn('API endpoint failed, falling back to direct Supabase query');
        }
      } catch (apiError) {
        console.warn('API endpoint error, falling back to direct Supabase query:', apiError);
      }

      // Fallback: Direct Supabase query
      console.log('DEBUG: Using direct Supabase query for user:', user.id);
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('unlock_count')
        .eq('id', user.id)
        .single();

      if (userError) {
        console.error('DEBUG: Error fetching user data:', userError);
        throw userError;
      }

      console.log('DEBUG: Direct Supabase query result:', userData);
      setUnlockCount(userData?.unlock_count || 0);
      setUserCredits(userData?.credits || 0);

      // Also fetch unlocked profile IDs directly
      const { data: unlockedData, error: unlockedError } = await supabase
        .from('unlocked_profiles')
        .select('profile_id')
        .eq('user_id', user.id);

      if (!unlockedError && unlockedData) {
        const unlockedIds = unlockedData.map(item => item.profile_id);
        console.log('DEBUG: Unlocked profile IDs:', unlockedIds);
        setUnlockedProfileIds(new Set(unlockedIds));
      }

    } catch (error) {
      console.error("Error fetching user unlock info:", error);
      // Set default values on error
      setUnlockCount(0);
      setUnlockedProfileIds(new Set());
    }
  }, []);

  // Load every school with at least one Accepted or Committed result for the filter.
  const fetchAvailableColleges = useCallback(async () => {
    try {
      let from = 0;
      let allResults = [];

      while (true) {
        const { data: page, error: queryError } = await supabase
          .from('profiles')
          .select('college_results')
            .eq('is_archived', false)
          .range(from, from + PROFILE_PAGE_SIZE - 1);

        if (queryError) throw queryError;
        allResults = allResults.concat(page || []);
        if (!page || page.length < PROFILE_PAGE_SIZE) break;
        from += PROFILE_PAGE_SIZE;
      }

      const colleges = new Set();
      allResults.forEach(({ college_results: results }) => {
        if (!results || typeof results !== 'object') return;
        Object.entries(results).forEach(([school, status]) => {
          if (typeof status === 'string' && ['accepted', 'committed'].includes(status.toLowerCase())) {
            colleges.add(school);
          }
        });
      });
      setAvailableColleges(Array.from(colleges).sort((a, b) => a.localeCompare(b)));
    } catch (e) {
      console.error('Failed to load school filter options:', e);
    }
  }, []);

  // Initial fetches
  useEffect(() => {
    fetchUserStatus();
    fetchUserUnlockInfo();
    fetchLeaderboard();
  }, [fetchUserStatus, fetchUserUnlockInfo, fetchLeaderboard]); // Rerun if limit changes via fetchLeaderboard dependency

  useEffect(() => {
    fetchAvailableColleges();
  }, [fetchAvailableColleges]);

  const handleCollegeFilterChange = (event) => {
    const college = event.target.value;
    setFilterCollege(college);
    if (college) setLimit('all');
  };

  // --- Filtering Logic ---
  const filteredLeaders = useMemo(() => {
    if (!filterCollege) {
      return leaders; // No filter applied
    }
    return leaders.filter(profile => {
      const results = profile.college_results;
      if (!results || typeof results !== 'object') {
        return false; // Cannot filter if no results object
      }
      const status = results[filterCollege];
      // Include if status is 'Accepted' or 'Committed' (case-insensitive check)
      return status && (status.toLowerCase() === 'accepted' || status.toLowerCase() === 'committed');
    });
  }, [leaders, filterCollege]); // Re-run when leaders or filter changes

  // --- Define renderStatsLine here, outside the map ---
  const renderStatsLine = (p) => {
    // Ensure 'p' has the expected properties (gpa, sat_score, act_score)
    if (!p) return 'Stats N/A'; // Handle case where profile data might be missing

    const parts = [
      p.gpa ? `GPA: ${p.gpa?.toFixed(2)}` : null, // Add optional chaining just in case
      p.sat_score ? `SAT: ${p.sat_score}` : null,
      p.act_score ? `ACT: ${p.act_score}` : null,
    ].filter(Boolean); // Filter out null/undefined values

    return parts.length > 0 ? parts.join(' | ') : 'Stats N/A';
  };
  // --- End defining renderStatsLine ---

  // Helper to render a single school icon/fallback
  const renderSchoolIcon = (collegeName) => {
    const logoPath = getLogoPath(collegeName);
    
    let logoStyle = { ...styles.collegeLogo }; // Start with base styles
    
    // --- Update conditional scaling ---
    if (collegeName === 'Brown') {
      // Specific, larger scaling for Brown
      logoStyle.transform = 'scale(1.5)'; // Increased scale factor for Brown (e.g., 1.5 = 150%)
    } else if (collegeName === 'Yale' || collegeName === 'Dartmouth' || collegeName === 'UPenn') { 
      // Keep previous scaling for the other specific schools
      logoStyle.transform = 'scale(1.3)'; 
    }
    // --- End conditional scaling ---

    return (
      <div style={styles.collegeLogoContainer} className="college-logo-container" title={collegeName}> 
        {logoPath ? (
          <img
            src={logoPath}
            alt={`${collegeName} logo`}
            style={logoStyle} // Apply the potentially modified style
            onError={(e) => {
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'inline-flex'; 
              e.target.style.display = 'none'; 
              e.target.onerror = null; 
            }}
          />
        ) : null}
        <span 
          style={{ ...styles.collegeLogoFallback, display: logoPath ? 'none' : 'inline-flex' }}
          className="college-logo-fallback"
        >
          {getCollegeInitials(collegeName)}
        </span>
      </div>
    );
  };


  // Helper function to render college results section
  const renderCollegeResults = (collegeResults) => {
    // Handle null, undefined, or non-object results gracefully
    if (!collegeResults || typeof collegeResults !== 'object' || Object.keys(collegeResults).length === 0) {
      return <p style={styles.collegeResultDetail}>No results shared.</p>;
    }

    // Find committed school name
    const committedSchoolName = Object.entries(collegeResults).find(([_, status]) =>
        typeof status === 'string' && status.toLowerCase() === 'committed')?.[0];

    // Get all schools where status is 'Accepted' or 'Committed'
    const acceptedOrCommittedSchools = Object.entries(collegeResults)
      .filter(([_, status]) =>
          typeof status === 'string' && ['accepted', 'committed'].includes(status.toLowerCase()))
      .map(([school]) => school);

    // If no accepted or committed schools found
    if (acceptedOrCommittedSchools.length === 0) {
       return <p style={styles.collegeResultDetail}>No acceptances listed.</p>;
    }

    return (
      // Use the container style for flex wrapping
      <div style={styles.collegeResultsContainer} className="college-results-container">
         {acceptedOrCommittedSchools.map(school => (
           // Render each school icon wrapper
           <div key={school} style={styles.acceptedSchoolWrapper} title={school}> {/* Add title for tooltip */}
             {renderSchoolIcon(school)} {/* This function already uses collegeLogoContainer style */}
             {/* Add checkmark conditionally */}
             {school === committedSchoolName && (
               <span style={styles.committedCheckmark} className="committed-checkmark">✓</span>
             )}
           </div>
         ))}
      </div>
    );
  };

  // Function to handle unlocking essays with credits
  const handleUnlockEssays = async (profileId, leaderboardRank) => {
    if (!currentUserId) {
      alert("You must be logged in to unlock essays.");
      return;
    }

    try {
      console.log(`Attempting to unlock essays for profile ${profileId} for user ${currentUserId}`);
      
      // Check if user owns the profile (free access)
      const { data: ownProfile, error: ownProfileError } = await supabase
        .from('profiles')
        .select('id')
            .eq('is_archived', false)
        .eq('id', profileId)
        .eq('user_id', currentUserId)
        .maybeSingle();

      if (ownProfileError) throw ownProfileError;
      
      if (ownProfile) {
        alert('This is your own profile - access granted for free!');
        setUnlockedProfileIds(prev => new Set(prev).add(profileId));
        return;
      }

      // Check if already unlocked
      const { data: existingUnlock, error: checkError } = await supabase
        .from('unlocked_profiles')
        .select('*')
        .eq('user_id', currentUserId)
        .eq('profile_id', profileId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingUnlock) {
        alert('Essays already unlocked!');
        setUnlockedProfileIds(prev => new Set(prev).add(profileId));
        return;
      }

      // Get profile data for pricing
      const profile = filteredLeaders.find(p => p.id === profileId);
      if (!profile) {
        throw new Error('Profile not found');
      }

      // Check if user can afford it (use credits or unlocks as fallback)
      const totalCredits = userCredits || unlockCount;
      const affordabilityCheck = canAffordEssays(totalCredits, leaderboardRank, profile);
      
      if (!affordabilityCheck.canAfford) {
        const breakdown = getDetailedPricingBreakdown(leaderboardRank, profile);
        setConfirmModalData({
          type: 'insufficient',
          cost: affordabilityCheck.cost,
          userCredits: affordabilityCheck.userCredits,
          shortfall: affordabilityCheck.shortfall,
          tier: breakdown.tier,
          rank: leaderboardRank,
          essayCount: breakdown.essayCount
        });
        setShowConfirmModal(true);
        return;
      }

      // Show pricing breakdown and confirm
      const breakdown = getDetailedPricingBreakdown(leaderboardRank, profile);
      setConfirmModalData({
        type: 'confirm',
        cost: affordabilityCheck.cost,
        breakdown: breakdown,
        totalCredits: totalCredits,
        profileId: profileId,
        onConfirm: () => proceedWithUnlock(profileId, affordabilityCheck.cost, totalCredits)
      });
      setShowConfirmModal(true);
    } catch (error) {
      console.error("Error unlocking essays:", error);
      alert(`Unlock failed: ${error.message}`);
    }
  };

  const proceedWithUnlock = async (profileId, cost, totalCredits) => {
    try {
      setShowConfirmModal(false);

      // Debug logging
      console.log('DEBUG: proceedWithUnlock called with:', { profileId, cost, totalCredits, userCredits, unlockCount });

      // Use the appropriate unlock function based on what credits we have
      if (userCredits > 0) {
        console.log('DEBUG: Using new credit system (unlock_essays_with_credits)');
        // Use new credit system
        const { data, error: unlockError } = await supabase.rpc('unlock_essays_with_credits', {
          user_id_param: currentUserId,
          profile_id_param: profileId,
          credit_cost: cost
        });

        console.log('DEBUG: unlock_essays_with_credits result:', { data, error: unlockError });

        if (unlockError) {
          if (unlockError.message.includes('Insufficient credits')) {
            alert('Insufficient credits! Please purchase more credits.');
          } else {
            throw unlockError;
          }
          return;
        }

        console.log('DEBUG: Updating userCredits from', userCredits, 'to', userCredits - cost);
        setUserCredits(prev => prev - cost);
      } else {
        console.log('DEBUG: Using old unlock system (use_unlock)');
        // Use old unlock system as credits - now with variable cost support
        const { data, error: unlockError } = await supabase.rpc('use_unlock', {
          user_id_param: currentUserId,
          profile_id_param: profileId,
          credit_cost: cost  // Pass the actual cost instead of defaulting to 1
        });

        console.log('DEBUG: use_unlock result:', { data, error: unlockError });

        if (unlockError) {
          throw unlockError;
        }

        console.log('DEBUG: Updating unlockCount from', unlockCount, 'to', unlockCount - cost);
        setUnlockCount(prev => Math.max(0, prev - cost));
      }

      // Success!
      showSuccessMessage(`📚 Essays unlocked successfully! Used ${cost} credits. You have ${totalCredits - cost} credits remaining.`);
      setUnlockedProfileIds(prev => new Set(prev).add(profileId)); // Add to unlocked set

    } catch (error) {
      console.error("Error unlocking essays:", error);
      alert(`Unlock failed: ${error.message}`);
    }
  };

  // Function to handle unlocking a profile (old system - keep for backwards compatibility)
  const handleUnlockProfile = async (profileId) => {
    if (unlockCount < 1) {
      alert("You don't have enough unlocks.");
      return;
    }

    if (!currentUserId) {
      alert("You must be logged in to unlock profiles.");
      return;
    }

    try {
      console.log(`Attempting to unlock profile ${profileId} for user ${currentUserId}`);
      
      // Check if user owns the profile (free unlock)
      const { data: ownProfile, error: ownProfileError } = await supabase
        .from('profiles')
        .select('id')
            .eq('is_archived', false)
        .eq('id', profileId)
        .eq('user_id', currentUserId)
        .maybeSingle();

      if (ownProfileError) throw ownProfileError;
      
      if (ownProfile) {
        showSuccessMessage('🎉 This is your own profile - access granted for free!');
        setUnlockedProfileIds(prev => new Set(prev).add(profileId));
        return;
      }

      // Check if already unlocked
      const { data: existingUnlock, error: checkError } = await supabase
        .from('unlocked_profiles')
        .select('*')
        .eq('user_id', currentUserId)
        .eq('profile_id', profileId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingUnlock) {
        showSuccessMessage('✅ Profile already unlocked!');
        setUnlockedProfileIds(prev => new Set(prev).add(profileId));
        return;
      }

      // Use the database function to spend an unlock (1 credit for old system)
      const { data, error: unlockError } = await supabase.rpc('use_unlock', {
        user_id_param: currentUserId,
        profile_id_param: profileId,
        credit_cost: 1  // Old system always costs 1 credit
      });

      if (unlockError) {
        if (unlockError.message.includes('Insufficient unlocks')) {
          alert('You don\'t have enough unlocks!');
        } else {
          throw unlockError;
        }
        return;
      }

      // Success!
      showSuccessMessage(`🔓 Profile unlocked successfully! You have ${(userCredits || unlockCount) - 1} credits remaining.`);
      // Update the appropriate counter
      if (userCredits > 0) {
        setUserCredits(prev => Math.max(0, prev - 1));
      } else {
        setUnlockCount(prev => Math.max(0, prev - 1));
      }
      setUnlockedProfileIds(prev => new Set(prev).add(profileId)); // Add to unlocked set
      console.log(`Profile ${profileId} unlocked. Remaining credits: ${(userCredits || unlockCount) - 1}`);

    } catch (error) {
      console.error("Error unlocking profile:", error);
      alert(`Unlock failed: ${error.message}`);
    }
  };

  // Main Render Logic
  if (loading || checkingUserStatus) {
      return <div style={styles.loadingError}>Loading leaderboard... <span className="spinner"></span></div>;
  }

  if (error) {
      return <div style={{ ...styles.loadingError, ...styles.errorText }}>Error: {error}</div>;
  }

  return (
    <div style={styles.leaderboard} className={`leaderboard-container ${isDarkMode ? 'dark' : ''}`}>
      {/* Success Message Banner */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#28a745',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 1000,
          maxWidth: '400px',
          fontSize: '0.95rem',
          animation: 'slideInFromRight 0.3s ease-out'
        }}>
          {successMessage}
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {showConfirmModal && confirmModalData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-color, #ffffff)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            border: '1px solid var(--border-color, #e1e5e9)'
          }}>
            {confirmModalData.type === 'insufficient' ? (
              <>
                <h3 style={{ 
                  color: 'var(--error-color, #dc3545)', 
                  marginBottom: '1rem', 
                  fontSize: '1.3rem',
                  textAlign: 'center'
                }}>
                  💸 Insufficient Credits
                </h3>
                <div style={{ marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  <p><strong>Cost:</strong> {confirmModalData.cost} credits</p>
                  <p><strong>You have:</strong> {confirmModalData.userCredits} credits</p>
                  <p><strong>Shortfall:</strong> {confirmModalData.shortfall} credits</p>
                  <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid var(--border-color, #e1e5e9)' }} />
                  <p><strong>Profile tier:</strong> {confirmModalData.tier.label} (#{confirmModalData.rank})</p>
                  <p><strong>Essays:</strong> {confirmModalData.essayCount} total</p>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--button-secondary-bg, #6c757d)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowConfirmModal(false);
                      navigate('/purchase-unlocks');
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--accent-secondary, #28a745)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Buy More Credits
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ 
                  color: 'var(--accent-color, #007bff)', 
                  marginBottom: '1rem', 
                  fontSize: '1.3rem',
                  textAlign: 'center'
                }}>
                  🔓 Unlock Essays
                </h3>
                <div style={{ marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                    <strong>Cost: {confirmModalData.cost} credits</strong>
                  </p>
                  <div style={{ 
                    backgroundColor: 'var(--bg-neutral-alt, #f3f4f6)', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Breakdown:</h4>
                    {confirmModalData.breakdown.breakdown.map((item, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        marginBottom: '0.25rem'
                      }}>
                        <span>{item.item}</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {item.cost > 0 ? '+' : ''}{item.cost} credits
                        </span>
                      </div>
                    ))}
                    <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid var(--border-color, #e1e5e9)' }} />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>
                      <span>Total</span>
                      <span>{confirmModalData.breakdown.total} credits</span>
                    </div>
                  </div>
                  <p>
                    <strong>Your credits:</strong> {confirmModalData.totalCredits} → {confirmModalData.totalCredits - confirmModalData.breakdown.total}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--button-secondary-bg, #6c757d)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmModalData.onConfirm}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--accent-color, #007bff)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    Unlock for {confirmModalData.cost} Credits
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      <h2 style={styles.title}>
        {limit === 'all' ? `Profile Rankings (All ${leaders.length})` : `Profile Rankings (Top ${limit})`}
      </h2>
      <div style={styles.filterSection}>
        <div style={styles.filterContainer} className="filter-container">
          <label htmlFor="collegeFilter" style={styles.filterLabel} className="filter-label">
            School
          </label>
          <select
            id="collegeFilter"
            value={filterCollege}
            onChange={handleCollegeFilterChange}
            style={styles.filterSelect}
            aria-label="Filter profiles by accepted or committed school"
          >
            <option value="">All Schools</option>
            {availableColleges.map(college => (
              <option key={college} value={college}>{college}</option>
            ))}
          </select>
        </div>
        {filterCollege && (
          <p style={styles.filterMessage}>
            Showing profiles accepted or committed to {filterCollege} ({filteredLeaders.length})
          </p>
        )}
      </div>
      <div style={{ textAlign: 'center', margin: '-0.5rem 0 1.5rem' }}>
        <button
          type="button"
          onClick={() => setLimit(current => current === 'all' ? DEFAULT_PROFILE_LIMIT : 'all')}
          disabled={loading}
          aria-expanded={limit === 'all'}
        >
          {limit === 'all' ? 'Show Top 25' : 'View All Profiles'}
        </button>
      </div>
      


      {/* --- Use filteredLeaders for display --- */}
      {filteredLeaders.length === 0 ? (
        <p style={styles.emptyList}>
          {filterCollege
            ? `No profiles found with acceptances to ${filterCollege}.`
            : 'No profiles ranked yet, or none match the criteria.'
          }
        </p>
      ) : (
        <ol style={styles.leaderboardList} className="leaderboard-list">
          {/* --- Map over filteredLeaders --- */}
          {filteredLeaders.map((profile, index) => {
            const isUnlocked = unlockedProfileIds.has(profile.id);
            const isOwnProfile = profile.user_id === currentUserId;
            const canViewFull = isUnlocked || isOwnProfile;

            return (
              <li key={profile.id} style={styles.leaderboardItem} className="leaderboard-item">
                <div style={styles.rank} className="rank">{index + 1}</div>
                <div style={styles.profileInfo}>
                  {/* Profile Header */}
                  <h3 style={styles.profileHeader} className="profile-header">
                    Profile (Class of {profile.class_year || 'N/A'})
                  </h3>

                  {/* Essay Excerpt */}
                  {profile.essay && (
                     <p style={styles.profileDetail} className="profile-detail">
                        <span style={styles.profileLabel}>Essay Excerpt:</span> {`${profile.essay.slice(0, 120)}...`}
                     </p>
                  )}

                  {/* Stats */}
                  <p style={styles.profileDetail} className="profile-detail">
                     <span style={styles.profileLabel}>Stats:</span> {renderStatsLine(profile)}
                  </p>

                  {/* ECs */}
                  {profile.extracurriculars && Array.isArray(profile.extracurriculars) && profile.extracurriculars.length > 0 && (
                    <div>
                      <span style={styles.profileLabel}>Top ECs:</span>
                      <ul style={styles.ecList} className="ec-list">
                        {profile.extracurriculars.map((ec, ecIndex) => (
                          <li key={ecIndex} style={styles.ecListItem}>{ec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Awards */}
                  {profile.awards && (
                    <p style={styles.profileDetail} className="profile-detail">
                      <span style={styles.profileLabel}>Awards/Honors:</span> {profile.awards}
                    </p>
                   )}

                  {/* College Results Section */}
                  <div style={styles.collegeResultsSection} className="college-results-section">
                    <h4 style={styles.collegeResultsTitle} className="college-results-title">Acceptances:</h4>
                    {renderCollegeResults(profile.college_results)}
                  </div>

                  {/* Rating */}
                  <span style={styles.rating} className="rating">Rating: {Math.round(profile.rating)}</span>

                  {/* Essay Pricing */}
                  <div style={styles.essayPricing} className="essay-pricing">
                    <span style={styles.essayPricingLabel}>Essays:</span>
                    <span style={styles.essayPricingValue}>
                      {getPricingDisplayText(index + 1, profile)}
                    </span>
                  </div>

                  {/* Unlock/View Button - Updated Text */}
                  {canViewFull ? (
                    <Link to={`/profile/${profile.id}`} style={styles.viewProfileButton} className="view-profile-button">
                      View Full Profile
                    </Link>
                  ) : (
                    <>
                      <button
                        onClick={() => handleUnlockEssays(profile.id, index + 1)}
                        disabled={(userCredits || unlockCount) < 1}
                        style={{
                          ...styles.unlockButton,
                          ...((userCredits || unlockCount) < 1 ? styles.unlockButtonDisabled : {})
                        }}
                        className={`unlock-button ${(userCredits || unlockCount) < 1 ? 'button-disabled' : ''}`}
                      >
                        Unlock Essays
                      </button>
                      <span style={styles.unlockInfo}>
                        (You have {userCredits || unlockCount} credits remaining)
                        {(userCredits || unlockCount) < 10 && (
                          <button
                            onClick={() => navigate('/purchase-unlocks')}
                            style={{
                              marginLeft: '0.5rem',
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.8rem',
                              backgroundColor: 'var(--accent-secondary, #28a745)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Buy More
                          </button>
                        )}
                      </span>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* Debug Info and Refresh Buttons */}
      <div style={{ textAlign: 'center', marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-neutral-alt, #f3f4f6)', borderRadius: '8px' }}>
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--text-medium, #666)' }}>
          <strong>Debug Info:</strong> Current User ID: {currentUserId || 'Not logged in'} | Unlock Count: {unlockCount}
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={fetchLeaderboard}
            disabled={loading}
            style={styles.refreshButton}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.refreshButtonHover.backgroundColor}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.refreshButton.backgroundColor}
          >
            {loading ? 'Refreshing...' : 'Refresh Rankings'}
          </button>

          <button
            onClick={fetchUserUnlockInfo}
            disabled={loading}
            style={{
              ...styles.refreshButton,
              backgroundColor: 'var(--accent-secondary, #28a745)'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-secondary, #28a745)'}
          >
            Refresh Unlock Count
          </button>

          <button
            onClick={fetchUserStatus}
            disabled={loading || checkingUserStatus}
            style={{
              ...styles.refreshButton,
              backgroundColor: 'var(--accent-color, #007bff)'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0069d9'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-color, #007bff)'}
          >
            {checkingUserStatus ? 'Checking...' : 'Refresh Status'}
          </button>
        </div>
      </div>

       {/* Optional: Control to change the limit */}
       {/*
       <div style={{textAlign: 'center', marginTop: '1rem'}}>
         <label htmlFor="limitSelect">Show Top: </label>
         <select
           id="limitSelect"
           value={limit}
           onChange={(e) => setLimit(parseInt(e.target.value, 10))}
           disabled={loading}
         >
           <option value={10}>10</option>
           <option value={25}>25</option>
           <option value={50}>50</option>
           <option value={100}>100</option>
         </select>
       </div>
       */}
    </div>
  );
  }
